import { responsive } from '@theme/responsive';
import { RevolutionAnimation } from '@theme/revolution-animation';

/**
 * Main blog: chronological responsive masonry, AJAX load-more, and scroll restore.
 *
 * Contract:
 * - `.main-blog__grid` contains a flat chronological list of direct
 *   `.main-blog__article-card` children.
 * - Mobile keeps that source order in one column.
 * - Desktop positions odd/even cards in independent left/right masonry tracks without
 *   changing DOM order. CSS provides a chronological row-aligned fallback without JS.
 */
class MainBlogSection extends HTMLElement {
  #grid;
  #pagination;
  #status;
  #error;
  #onLayoutChange;
  #onClick;
  #resizeListener;
  #cardResizeObserver;
  #abortController;
  #rafId = 0;
  #loading = false;
  // Going back: how many pages are currently in the DOM (page 1 is server-rendered) +
  // listeners that persist/restore the loaded pages and scroll position across navigation.
  #loadedPages = 1;
  #scrollSettleObserver;
  #scrollSettleTimeout;
  #onPageHide;
  #onPageShow;
  #restoreAbort;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#grid = this.querySelector('.main-blog__grid');
    if (!this.#grid) return;

    this.#pagination = this.querySelector('.main-blog__pagination');
    this.#status = this.querySelector('[role="status"]');
    this.#error = this.querySelector('[data-main-blog-load-more-error]');

    this.#onLayoutChange = () => this.#scheduleLayout();
    this.#resizeListener = responsive.addResizeListener(this, this.#onLayoutChange);

    if ('ResizeObserver' in window) {
      this.#cardResizeObserver = new ResizeObserver(this.#onLayoutChange);
      this.#observeCards();
    }

    // Delegated so the swapped-in load-more button keeps working.
    this.#onClick = (event) => this.#handleClick(event);
    this.addEventListener('click', this.#onClick);

    this.#updateCount();

    // Going back: own the scroll restoration so we can re-append the pages the visitor
    // had loaded (and re-pin scroll) when they return from an article. pagehide captures
    // the final scroll on navigation away — no continuous scroll listener needed.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    this.#onPageHide = () => this.#persistState();
    this.#onPageShow = (event) => {
      // bfcache restore: DOM + scroll come back intact, only re-pin the scroll.
      if (event.persisted) {
        this.#layoutMasonry();
        this.#restoreScroll(this.#readState()?.y);
        this.#reveal();
      }
    };
    // delegate-exempt: pagehide/pageshow are window lifecycle events, not delegable DOM events.
    window.addEventListener('pagehide', this.#onPageHide);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#restoreState();
  }

  disconnectedCallback() {
    this.initialized = false;
    this.#resizeListener?.removeResizeListener();
    this.#cardResizeObserver?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.#abortController?.abort();
    this.#restoreAbort?.abort();
    cancelAnimationFrame(this.#rafId);
    this.#stopScrollSettle();
    window.removeEventListener('pagehide', this.#onPageHide);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /* ---------------------------------------------------------------- Load more */

  #handleClick(event) {
    const button = event.target.closest('[data-action="main-blog:load-more"]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    this.#loadMore(button);
  }

  async #loadMore(button) {
    if (this.#loading) return;
    const url = button.dataset.nextUrl || button.getAttribute('href');
    if (!url) return;

    this.#loading = true;
    const loader = button.querySelector('.itemLoader');
    button.classList.add('is-loading');
    loader?.classList.add('is-visible');
    button.setAttribute('aria-busy', 'true');
    this.#error?.setAttribute('hidden', '');

    // Cancel any in-flight request (rapid clicks / disconnect) so a stale response
    // can't mutate a detached or out-of-date grid.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');

      const newGrid = doc.querySelector('.main-blog__grid');
      const appendedCards = this.#appendCards(newGrid);
      if (!appendedCards.length) throw new Error('No blog cards found in response');

      // Fade the appended cards in with the shared reveal (matches sparv load-more.js) — the
      // scroll observer only wires up first-paint elements, so reveal these explicitly.
      this.#revealAppendedNodes(appendedCards);

      // Swap pagination so the load-more button updates (and disappears on the last page).
      const newPagination = doc.querySelector('.main-blog__pagination');
      if (this.#pagination && newPagination) {
        this.#pagination.innerHTML = newPagination.innerHTML;
        this.#error = this.querySelector('[data-main-blog-load-more-error]');
      }

      this.#loadedPages += 1;
      this.#persistState();

      this.#announce(appendedCards.length);
      this.#updateCount();
    } catch (error) {
      // Aborted requests are intentional (re-trigger / disconnect) — not an error to surface.
      if (error.name === 'AbortError') return;
      this.#error?.removeAttribute('hidden');
    } finally {
      this.#loading = false;
      button.classList.remove('is-loading');
      loader?.classList.remove('is-visible');
      button.removeAttribute('aria-busy');
    }
  }

  // Append fetched cards in source order; desktop masonry is presentation-only.
  #appendCards(
    newGrid,
    { stripAnimation = false, observeCards = true, scheduleLayout = true } = {}
  ) {
    if (!newGrid) return [];

    const appendedCards = Array.from(newGrid.children).filter((card) =>
      card.classList.contains('main-blog__article-card')
    );
    if (!appendedCards.length) return [];

    const fragment = document.createDocumentFragment();
    appendedCards.forEach((card) => {
      if (stripAnimation) card.classList.remove('animatedContent');
      fragment.appendChild(card);
    });
    this.#grid.appendChild(fragment);
    if (observeCards) this.#observeCards(appendedCards);
    if (scheduleLayout) this.#scheduleLayout();

    return appendedCards;
  }

  #revealAppendedNodes(nodes) {
    if (!window.theme?.enableAnimations) return;
    const cells = nodes.filter((node) => node.classList?.contains('animatedContent'));
    if (!cells.length) return;
    const anim = new RevolutionAnimation(null, true);
    anim.elementFadeIn(cells);
  }

  #announce(count) {
    if (!this.#status) return;
    const template = this.dataset.blogLoadMoreAnnouncement;
    this.#status.textContent = template
      ? template.replace('[count]', count).replace('{{ count }}', count)
      : '';
  }

  // "X out of Y posts" counter. The server renders a per-page approximation; correct it to
  // the exact number of rendered articles (grid cards + featured) so the count matches the DOM.
  #updateCount() {
    const countEl = this.querySelector('[data-blog-count]');
    if (!countEl) return;
    const template = countEl.dataset.blogCountTemplate;
    if (!template) return;
    const total = parseInt(countEl.dataset.blogTotal, 10) || 0;
    const featured = this.querySelector('.main-blog__featured-article') ? 1 : 0;
    const shown = Math.min(
      this.#grid.querySelectorAll('.main-blog__article-card').length + featured,
      total
    );
    countEl.textContent = template.replace('[shown]', shown).replace('[total]', total);
  }

  /* -------------------------------------------------------------- Going back */

  // Key by list URL (sans page param) so each blog / tag view restores independently.
  #stateKey() {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    return `main-blog:return:${url.pathname}${url.search}`;
  }

  #persistState() {
    try {
      sessionStorage.setItem(
        this.#stateKey(),
        JSON.stringify({ pages: this.#loadedPages, y: Math.round(window.scrollY) })
      );
    } catch (error) {
      // sessionStorage can throw (private mode / quota) — restore is a nicety, ignore.
    }
  }

  #readState() {
    try {
      const raw = sessionStorage.getItem(this.#stateKey());
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  async #restoreState() {
    const saved = this.#readState();
    if (!saved) {
      this.#layoutMasonry();
      this.#reveal();
      return;
    }
    const targetPages = Math.max(1, parseInt(saved.pages, 10) || 1);

    // Re-append every page the visitor had loaded beyond the first (server-rendered) one.
    if (targetPages > 1) {
      this.#restoreAbort = new AbortController();
      for (let page = this.#loadedPages + 1; page <= targetPages; page += 1) {
        const ok = await this.#appendPage(page, this.#restoreAbort.signal);
        if (!ok) break;
      }
      this.#observeCards();
    }

    // Pack before pinning scroll so the restored coordinate is measured against the final grid.
    this.#layoutMasonry();
    this.#restoreScroll(saved.y);
    this.#reveal();
  }

  // Remove the first-paint cloak (see .main-blog--cloak in main-blog.css).
  #reveal() {
    this.classList.remove('main-blog--cloak');
  }

  async #appendPage(page, signal) {
    try {
      const response = await fetch(this.#pageUrl(page), { signal });
      if (!response.ok) return false;
      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const newGrid = doc.querySelector('.main-blog__grid');
      const appendedCards = this.#appendCards(newGrid, {
        stripAnimation: true,
        observeCards: false,
        scheduleLayout: false,
      });
      if (!appendedCards.length) return false;

      const newPagination = doc.querySelector('.main-blog__pagination');
      if (this.#pagination && newPagination) {
        this.#pagination.innerHTML = newPagination.innerHTML;
        this.#error = this.querySelector('[data-main-blog-load-more-error]');
      }

      this.#loadedPages = page;
      this.#updateCount();
      return true;
    } catch (error) {
      // Aborted (disconnect) or network failure — stop restoring, page 1 still stands.
      return false;
    }
  }

  #pageUrl(page) {
    const url = new URL(location.href);
    url.searchParams.set('page', page);
    return url.toString();
  }

  #restoreScroll(y) {
    if (!y) return;

    // Try to land at y. Returns true once the page is tall enough to reach it (so we can
    // stop correcting and never fight the user's own scrolling).
    // `behavior: 'instant'` is required: the page sets `scroll-behavior: smooth`, which would
    // otherwise animate this jump — the sync scrollY check would never confirm the landing and
    // the restore would drift as images load.
    const land = () => {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      return Math.abs(window.scrollY - y) <= 2;
    };

    if (land() || !('ResizeObserver' in window)) return;

    // Page isn't tall enough yet — appended pages + late-loading images keep growing the
    // body. Re-apply on each height change until we reach the target, then stop. A
    // ResizeObserver (not a scroll listener) keeps this layout-driven and jank-free.
    this.#stopScrollSettle();
    this.#scrollSettleObserver = new ResizeObserver(() => {
      if (land()) this.#stopScrollSettle();
    });
    this.#scrollSettleObserver.observe(document.body);
    // timeout: stop re-pinning once layout has settled, so a late reflow can't hijack the
    // user's scroll long after the return.
    this.#scrollSettleTimeout = setTimeout(() => this.#stopScrollSettle(), 1500);
  }

  #stopScrollSettle() {
    this.#scrollSettleObserver?.disconnect();
    this.#scrollSettleObserver = null;
    if (this.#scrollSettleTimeout) {
      clearTimeout(this.#scrollSettleTimeout);
      this.#scrollSettleTimeout = null;
    }
  }

  /* ----------------------------------------------------------------- Masonry */

  #observeCards(cards = this.#grid.querySelectorAll('.main-blog__article-card')) {
    if (!this.#cardResizeObserver) return;
    cards.forEach((card) => this.#cardResizeObserver.observe(card));
  }

  #scheduleLayout() {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#layoutMasonry());
  }

  #layoutMasonry() {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;

    const cards = Array.from(this.#grid.children).filter((child) =>
      child.classList.contains('main-blog__article-card')
    );

    if (!cards.length || !responsive.matchesBreakpoint('lap_and_up')) {
      this.#resetMasonry(cards);
      return;
    }

    // Read grid metrics before changing layout mode.
    const gridStyles = getComputedStyle(this.#grid);
    const paddingStart = parseFloat(gridStyles.paddingInlineStart) || 0;
    const paddingEnd = parseFloat(gridStyles.paddingInlineEnd) || 0;
    const columnGap = parseFloat(gridStyles.columnGap) || 0;
    const rowGap = parseFloat(gridStyles.rowGap) || 0;
    const contentWidth = Math.max(0, this.#grid.clientWidth - paddingStart - paddingEnd);
    const cardWidth = Math.max(0, (contentWidth - columnGap) / 2);
    if (!cardWidth) {
      this.#resetMasonry(cards);
      return;
    }

    // Write the shared width first, then read every resulting height together. No writes
    // happen inside the measurement loop, avoiding one forced layout per card.
    this.#grid.style.setProperty('--main-blog-masonry-card-width', `${cardWidth}px`);
    this.#grid.classList.add('is-masonry');
    const cardHeights = cards.map((card) => card.getBoundingClientRect().height);

    const columnHeights = [0, 0];
    const positions = cardHeights.map((height, index) => {
      const columnIndex = index % 2;
      const position = {
        inlineStart: paddingStart + columnIndex * (cardWidth + columnGap),
        top: columnHeights[columnIndex],
      };
      columnHeights[columnIndex] += height + rowGap;
      return position;
    });

    // Apply all positions in one write phase.
    cards.forEach((card, index) => {
      card.style.insetInlineStart = `${positions[index].inlineStart}px`;
      card.style.top = `${positions[index].top}px`;
    });

    const tallestColumn = Math.max(...columnHeights);
    this.#grid.style.height = `${Math.max(0, tallestColumn - rowGap)}px`;
  }

  #resetMasonry(cards) {
    this.#grid.classList.remove('is-masonry');
    this.#grid.style.removeProperty('--main-blog-masonry-card-width');
    this.#grid.style.height = '';
    cards.forEach((card) => {
      card.style.insetInlineStart = '';
      card.style.top = '';
    });
  }
}

if (!customElements.get('main-blog-section')) {
  customElements.define('main-blog-section', MainBlogSection);
}
